// Unread, read and empty, read with something in it — the one rule every
// screen draws its empty state from (#629, #637), `core/src/reading.rs`'s.
//
// An unread list is not an empty one. Every provider here answers some time
// after the screen is drawn, and before its first answer the list it hands
// over is empty because nothing has been read — which a screen that does not
// ask draws as "nothing booked" (#626, #629, #637).
//
// So `EmptyState` (src/components/EmptyState.jsx) takes a `reading` and
// draws the loading state in its place while it is `unread`, and
// test/ui/reading.test.jsx refuses an `<EmptyState` without one: a screen
// cannot show its empty state over a list nobody has read by forgetting.
// The phones carry the same answer as `Reading` (`domain/Reading.kt`,
// `Domain/Reading.swift`), each taking it from the same core function.
//
// **What a read is** is the provider's: its `loading` is false — the first
// answer from the store or the snapshot that `answerIsRead` below accepts
// (#207, #267, #651). A list holding something is `some` even while
// `loading`: a write queued before the first answer is on screen, and a
// spinner over it would hide it (#267).
//
// **An empty cache that never heard from the server is not a read (#651).**
// Otherwise a device that has never synced, started offline, takes its empty
// cache's first answer as the studio's: "You don't have any students yet" over
// twelve. The rule and why
// are in `core/src/reading.rs`; `src/data/useCollectionWindow.js` applies it
// to every collection window, both read arms, and `src/data/serverAnswers.js`
// keeps the device's marks and the set of lists still waiting for the server,
// which `Unread` (src/components/Unread.jsx) turns into the core's sentence.

import { coreSync } from './core.js';

export const READING = Object.freeze({
  unread: 'unread', empty: 'empty', some: 'some',
  // The read stopped on an error before it answered (#647).
  failed: 'failed',
});

// `loaded` is whether the list's provider has answered — `!loading`, or a
// provider's `loaded` — and `count` how many things it holds. Several reads
// behind one figure are passed as their conjunction. `failed` is the
// provider's `failed`: its read stopped on an error before answering (#647);
// several are passed as their disjunction.
export const reading = (loaded, count, failed = false) => coreSync().readingState(
  Boolean(loaded), Boolean(failed), Math.max(0, Number(count) || 0),
);

// Whether a listener's answer is a read (#651): `serverConfirmed` is whether
// the server has answered this list on this device — this answer, or one
// before it — and `count` how many things the answer holds.
export const answerIsRead = (serverConfirmed, count) => coreSync().readingAnswerIsRead(
  Boolean(serverConfirmed), Math.max(0, Number(count) || 0),
);

// The words, as the screens read them at render. Pinned to the core's
// `WORDING` by test/core/reading.wasm.test.js, as HOME_WORDING is to home's.
export const READING_WORDING = Object.freeze({
  loading: 'Loading…',
  unreadFigure: '—',
  failedTitle: 'This couldn’t be loaded',
  failedDescription: 'The read stopped before it answered. The connection may have dropped, or the server refused it.',
  retry: 'Try again',
  awaitingTitle: 'Not loaded on this device yet',
  awaitingDescription: 'The server hasn’t answered yet, and this device has no copy of your studio to show. It may be offline — this fills in by itself once the server answers.',
  // The sync chip while a list is waiting so (#682).
  awaitingChip: 'Waiting for the server',
  'students.emptyTitle': "You don't have any students yet",
  'students.emptyDescription': 'Add your students so you can schedule their lessons, take attendance, and invoice their family.',
  'students.noMatchesTitle': 'No matches',
  'students.noMatchesDescription': 'Nobody here matches the filter and the search. Clear the search or choose All statuses.',
  'families.emptyTitle': "You don't have any family accounts yet",
  'families.emptyDescription': "Family accounts appear here automatically once you've added students.",
  'family.noContacts': 'This account has no contacts, so nothing addressed to it can be sent.',
  'family.noTransactions': 'No transactions yet.',
  'family.noStudents': 'None',
  'invoices.emptyTitle': "You don't have any invoices yet",
  'invoices.emptyDescription': 'Generate invoices for one family or a whole cohort at once, covering any date range.',
  'invoices.noFamiliesDescription': "An invoice is for a family account, and family accounts appear once you've added students.",
  'transactions.emptyRange': 'No transactions in this range.',
  'chargeCategories.emptyTitle': 'Nothing on the ledger yet',
  'chargeCategories.emptyDescription': 'Charges appear here once you take attendance on a billable lesson or add a transaction by hand.',
  'student.accountEmpty': "Nothing on this family's account yet.",
  'student.practiceEmpty': 'No time has been logged yet.',
  'student.attendanceEmpty': 'No lessons scheduled for this student yet.',
  'student.repertoireEmpty': 'No repertoire assigned yet.',
  'packages.balancesEmptyTitle': 'No student holds a package yet',
  'packages.balancesEmptyDescription': 'Selling one from a template puts the student here.',
  'packages.templatesEmptyTitle': 'No package templates yet',
  'packages.templatesEmptyDescription': 'A package is sold from a template, so add one first.',
  'packages.activityEmpty': 'Nothing yet. Selling a package and teaching against it both show up here.',
  'locations.empty': 'No locations yet. Add the rooms or studios you teach in and they can be attached to events.',
  'noteTemplates.empty': 'No templates yet. Save the practice instructions you write every week and pick them instead of typing them again.',
  'event.noStudents': 'No students yet — add one first and they will appear here.',
  'addStudent.noFamilies': 'No family accounts yet — the first student creates one.',
});

// A figure — a tile's number, a count above a list — whose reads have not
// all answered shows the dash, not a zero.
export const figureOrDash = (known, figure) => (known ? figure : READING_WORDING.unreadFigure);
